import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  GoogleMap,
  Marker,
  StandaloneSearchBox,
  useJsApiLoader,
} from "@react-google-maps/api";
import { useSelector } from "react-redux";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../../config/googleMapsConfig";

const containerStyle = {
  width: "100%",
  height: "400px",
};

function GoogleMapsView({ lat, lng, address = "", onAddressChange, getAddress }) {
  const { theme } = useSelector((state) => state.theme);
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const mapRef = useRef(null);
  const searchBoxRef = useRef(null);

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);

  const center = {
    lat: lat || 5.5571096,
    lng: lng || -0.2012376,
  };

  const updateParentLocation = useCallback(
    (formattedAddress, latLng) => {
      if (typeof formattedAddress === "string" && onAddressChange) {
        onAddressChange(formattedAddress);
      }

      if (getAddress) {
        getAddress(formattedAddress, latLng);
      }
    },
    [getAddress, onAddressChange],
  );

  const updateCoordinatesOnly = useCallback(
    (latLng) => {
      if (getAddress) {
        getAddress(null, latLng);
      }
    },
    [getAddress],
  );

  const getAddressFromLatLng = useCallback(
    async (latValue, lngValue) => {
      if (!window.google?.maps?.Geocoder) return;

      const geocoder = new window.google.maps.Geocoder();

      try {
        const geocodeResult = await new Promise((resolve, reject) => {
          geocoder.geocode(
            {
              location: { lat: latValue, lng: lngValue },
            },
            (results, status) => {
              if (status === "OK") {
                resolve(results || []);
                return;
              }

              reject(new Error(`Geocoder failed due to: ${status}`));
            },
          );
        });

        if (geocodeResult[0]) {
          updateParentLocation(geocodeResult[0].formatted_address, {
            lat: latValue,
            lng: lngValue,
          });
        }
      } catch (error) {
        console.error("Geocoding error:", error);
      }
    },
    [updateParentLocation],
  );

  const onLoad = useCallback(
    (map) => {
      mapRef.current = map;

      const locationButtonDiv = document.createElement("div");

      locationButtonDiv.innerHTML = `
        <div style="background:white;padding:8px 12px;border-radius:8px;cursor:pointer;font-size:13px;box-shadow:0 2px 6px rgba(0,0,0,0.2);margin:10px;">
          Use current location
        </div>
      `;

      map.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(
        locationButtonDiv,
      );

      locationButtonDiv.addEventListener("click", () => {
        navigator.geolocation.getCurrentPosition((position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          setCurrentLocation(userLocation);
          setSelectedLocation(userLocation);
          map.panTo(userLocation);
          map.setZoom(16);

          updateCoordinatesOnly(userLocation);
          getAddressFromLatLng(userLocation.lat, userLocation.lng);
        });
      });

    },
    [getAddressFromLatLng, updateCoordinatesOnly],
  );

  const onUnmount = () => {
    mapRef.current = null;
  };

  useEffect(() => {
    if (lat && lng && mapRef.current) {
      const newCenter = { lat, lng };
      mapRef.current.panTo(newCenter);
      setSelectedLocation(newCenter);
    }
  }, [lat, lng]);

  const onSearchBoxLoad = useCallback((searchBox) => {
    searchBoxRef.current = searchBox;
  }, []);

  const onPlacesChanged = useCallback(() => {
    const places = searchBoxRef.current?.getPlaces?.();
    const place = places?.[0];

    if (!place?.geometry?.location) return;

    const nextLocation = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };

    setSelectedLocation(nextLocation);

    if (mapRef.current) {
      mapRef.current.panTo(nextLocation);
      mapRef.current.setZoom(16);
    }

    updateParentLocation(
      place.formatted_address || place.name || "",
      nextLocation,
    );
  }, [updateParentLocation]);

  const handleMapClick = useCallback(
    async (event) => {
      if (!event?.latLng) return;

      const latLng = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      setSelectedLocation(latLng);
      updateCoordinatesOnly(latLng);
      await getAddressFromLatLng(latLng.lat, latLng.lng);
    },
    [getAddressFromLatLng, updateCoordinatesOnly],
  );

  if (!isLoaded) return null;

  return (
    <div>
      <div className="mb-3">
        <label
          className={`block mb-1 ${
            theme === "light" ? "text-primaryColor" : "text-darkSecondary"
          }`}
        >
          Address
        </label>
        <StandaloneSearchBox
          onLoad={onSearchBoxLoad}
          onPlacesChanged={onPlacesChanged}
        >
          <input
            type="text"
            placeholder="Search address"
            value={address}
            onChange={(event) => onAddressChange?.(event.target.value)}
            className={`${
              theme === "light" ? "" : "bg-darkInput "
            } border p-2 text-sm w-full`}
          />
        </StandaloneSearchBox>
      </div>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={12}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleMapClick}
      >
        {selectedLocation && (
          <Marker
            position={selectedLocation}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: "red",
              fillOpacity: 1,
              strokeWeight: 2,
              strokeColor: "white",
            }}
          />
        )}

        {currentLocation && (
          <Marker
            position={currentLocation}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: "blue",
              fillOpacity: 1,
              strokeWeight: 2,
              strokeColor: "white",
            }}
          />
        )}
      </GoogleMap>

    </div>
  );
}

export default React.memo(GoogleMapsView);
